import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Badge,
    Table, Modal
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function DetailsReglement() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [reglement, setReglement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        fetchReglement();
    }, [id]);

    const fetchReglement = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setReglement(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleValider = async () => {
        setIsProcessing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'valider',
                        id: id
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowConfirmModal(false);
                fetchReglement();
            }
        } catch (err) {
            console.error("Erreur:", err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSupprimer = async () => {
        setIsProcessing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                navigate('/ventes/reglements');
            }
        } catch (err) {
            console.error("Erreur:", err);
        } finally {
            setIsProcessing(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            en_attente: { color: '#f59e0b', icon: 'ri-time-line', label: 'En attente' },
            valide: { color: '#10b981', icon: 'ri-check-double-line', label: 'Validé' },
            rejete: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Rejeté' }
        };
        const config = configs[statut] || configs.en_attente;
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const getModeIcon = (mode) => {
        const icons = {
            especes: 'ri-money-euro-circle-line',
            cheque: 'ri-bank-card-line',
            virement: 'ri-bank-line',
            carte_bancaire: 'ri-smartphone-line',
            traite: 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-card-line';
    };

    const getModeLabel = (mode) => {
        const labels = {
            especes: 'Espèces',
            cheque: 'Chèque',
            virement: 'Virement bancaire',
            carte_bancaire: 'Carte bancaire',
            traite: 'Traite'
        };
        return labels[mode] || mode;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du règlement...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !reglement) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Règlement non trouvé</h6>
                                <p className="mb-0 small">{error || "Le règlement demandé n'existe pas"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/ventes/reglements')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour à la liste
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bank-card-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Règlement {reglement.numero_reglement}</h4>
                                    <p className="text-muted small mb-0">Détails du règlement</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/ventes/reglements')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                            {reglement.statut === 'en_attente' && (
                                <Button 
                                    variant="success" 
                                    onClick={() => {
                                        setConfirmAction('valider');
                                        setShowConfirmModal(true);
                                    }}
                                    className="rounded-pill px-3 d-flex align-items-center gap-1"
                                    size="sm"
                                    style={{ background: '#10b981', border: 'none' }}
                                >
                                    <i className="ri-check-double-line"></i>
                                    Valider
                                </Button>
                            )}
                            <Button 
                                variant="outline-danger" 
                                onClick={() => {
                                    setConfirmAction('supprimer');
                                    setShowConfirmModal(true);
                                }}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-delete-bin-line"></i>
                                Supprimer
                            </Button>
                        </div>
                    </div>

                    {/* Carte d'identité */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-barcode-line text-primary fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">N° Règlement</small>
                                            <h4 className="mb-0">{reglement.numero_reglement}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-calendar-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Date</small>
                                            <h5 className="mb-0">{formatDate(reglement.date_reglement)}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-money-euro-circle-line text-info fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Montant</small>
                                            <h5 className="mb-0 text-primary fw-bold">{formatCurrency(reglement.montant)}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="text-end">{getStatutBadge(reglement.statut)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations principales */}
                    <Row className="g-4 mb-4">
                        <Col md={8}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-information-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Informations générales
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <div className="info-row">
                                                <span className="info-label">
                                                    <i className="ri-file-text-line me-2"></i>
                                                    N° Règlement
                                                </span>
                                                <span className="info-value fw-semibold">{reglement.numero_reglement}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">
                                                    <i className="ri-calendar-line me-2"></i>
                                                    Date
                                                </span>
                                                <span className="info-value">{formatDate(reglement.date_reglement)}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">
                                                    <i className={`${getModeIcon(reglement.mode_reglement)} me-2`}></i>
                                                    Mode
                                                </span>
                                                <span className="info-value">
                                                    <span className="mode-badge">
                                                        <i className={`${getModeIcon(reglement.mode_reglement)} me-1`}></i>
                                                        {getModeLabel(reglement.mode_reglement)}
                                                    </span>
                                                </span>
                                            </div>
                                            {reglement.reference && (
                                                <div className="info-row">
                                                    <span className="info-label">
                                                        <i className="ri-qr-code-line me-2"></i>
                                                        Référence
                                                    </span>
                                                    <span className="info-value">{reglement.reference}</span>
                                                </div>
                                            )}
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-row">
                                                <span className="info-label">
                                                    <i className="ri-money-euro-circle-line me-2"></i>
                                                    Montant
                                                </span>
                                                <span className="info-value fw-bold text-primary fs-5">{formatCurrency(reglement.montant)}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">
                                                    <i className="ri-user-line me-2"></i>
                                                    Saisi par
                                                </span>
                                                <span className="info-value">{reglement.prenom_utilisateur} {reglement.nom_utilisateur}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">
                                                    <i className="ri-time-line me-2"></i>
                                                    Date création
                                                </span>
                                                <span className="info-value">{formatDate(reglement.date_creation)}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    
                                    {reglement.commentaire && (
                                        <div className="comment-box mt-3">
                                            <div className="comment-header">
                                                <i className="ri-chat-1-line me-2"></i>
                                                Commentaire
                                            </div>
                                            <div className="comment-content">
                                                {reglement.commentaire}
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={4}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-user-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Client
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="client-avatar mb-3">
                                        <div className="avatar-circle">
                                            <i className="ri-user-line fs-2"></i>
                                        </div>
                                    </div>
                                    <h5 className="fw-bold text-center mb-2">{reglement.client_nom}</h5>
                                    <div className="client-details mt-3">
                                        <p className="mb-2">
                                            <i className="ri-barcode-line me-2 text-muted"></i>
                                            Code: {reglement.code_client}
                                        </p>
                                        {reglement.client_telephone && (
                                            <p className="mb-2">
                                                <i className="ri-phone-line me-2 text-muted"></i>
                                                {reglement.client_telephone}
                                            </p>
                                        )}
                                        {reglement.client_email && (
                                            <p className="mb-2">
                                                <i className="ri-mail-line me-2 text-muted"></i>
                                                {reglement.client_email}
                                            </p>
                                        )}
                                    </div>
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        className="w-100 mt-3 rounded-pill"
                                        onClick={() => navigate(`/ventes/clientsDetails/${reglement.id_client}`)}
                                        style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                    >
                                        <i className="ri-eye-line me-2"></i>
                                        Voir la fiche client
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>   
                    </Row>         

                    {/* Facture associée */}
                    {reglement.id_facture && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="bg-transparent border-0 pt-4">
                                <h6 className="mb-0 fw-semibold">
                                    <i className="ri-file-list-3-line me-2" style={{ color: '#21ACCD' }}></i>
                                    Facture associée
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <div className="info-row">
                                            <span className="info-label">
                                                <i className="ri-barcode-line me-2"></i>
                                                N° Facture
                                            </span>
                                            <span className="info-value">
                                                <Link to={`/ventes/facturesDetails/${reglement.id_facture}`} className="table-link">
                                                    {reglement.numero_facture}
                                                </Link>
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">
                                                <i className="ri-calculator-line me-2"></i>
                                                Montant TTC
                                            </span>
                                            <span className="info-value fw-bold">{formatCurrency(reglement.facture_montant)}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="info-row">
                                            <span className="info-label">
                                                <i className="ri-funds-line me-2"></i>
                                                Solde avant
                                            </span>
                                            <span className="info-value">{formatCurrency((reglement.facture_solde || 0) + (reglement.montant || 0))}</span>
                                        </div>
                                        <div className="info-row">
                                            <span className="info-label">
                                                <i className="ri-check-double-line me-2 text-success"></i>
                                                Solde après
                                            </span>
                                            <span className="info-value fw-bold text-primary fs-5">{formatCurrency(reglement.facture_solde || 0)}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* Modal de confirmation moderne */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                {confirmAction === 'valider' && (
                                    <i className="ri-check-double-line text-success" style={{ fontSize: '4rem' }}></i>
                                )}
                                {confirmAction === 'supprimer' && (
                                    <i className="ri-delete-bin-line text-danger" style={{ fontSize: '4rem' }}></i>
                                )}
                            </div>
                            <h3 className="fw-bold">
                                {confirmAction === 'valider' ? 'Valider le règlement' : 'Supprimer le règlement'}
                            </h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    {confirmAction === 'valider' ? (
                        <>
                            <p className="text-muted mb-4">
                                Êtes-vous sûr de vouloir valider ce règlement ?
                            </p>
                            <Alert variant="info" className="text-start rounded-3">
                                <div className="d-flex align-items-center">
                                    <i className="ri-information-line fs-4 me-3"></i>
                                    <div>
                                        <small className="d-block">✓ Le solde du client sera mis à jour</small>
                                        <small className="d-block">✓ Le statut de la facture sera mis à jour</small>
                                    </div>
                                </div>
                            </Alert>
                        </>
                    ) : (
                        <>
                            <p className="text-muted mb-4">
                                Êtes-vous sûr de vouloir supprimer ce règlement ?
                            </p>
                            <Alert variant="danger" className="rounded-3">
                                <i className="ri-alert-line me-2"></i>
                                Cette action est irréversible.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button 
                        variant={confirmAction === 'valider' ? 'success' : 'danger'} 
                        onClick={confirmAction === 'valider' ? handleValider : handleSupprimer}
                        disabled={isProcessing}
                        className="px-5 rounded-pill"
                    >
                        {isProcessing ? <Spinner size="sm" className="me-2" /> : null}
                        Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.4rem 1rem;
                    border-radius: 30px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                
                .info-row:last-child {
                    border-bottom: none;
                }
                
                .info-label {
                    display: flex;
                    align-items: center;
                    color: #64748b;
                    font-size: 0.85rem;
                }
                
                .info-value {
                    font-weight: 500;
                    color: #1e293b;
                }
                
                .mode-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    background: #e2f3f5;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    color: #21ACCD;
                }
                
                .comment-box {
                    margin-top: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border-left: 3px solid #21ACCD;
                }
                
                .comment-header {
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #21ACCD;
                }
                
                .comment-content {
                    font-size: 0.85rem;
                    color: #475569;
                }
                
                .client-avatar {
                    display: flex;
                    justify-content: center;
                }
                
                .avatar-circle {
                    width: 70px;
                    height: 70px;
                    background: linear-gradient(135deg, #21ACCD20, #1a8ba820);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                
                .client-details {
                    padding: 0.5rem 0;
                }
                
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                
                .table-link:hover {
                    text-decoration: underline;
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}